// =====================================================================
// src/lib/browserNotifications.ts — iSentiAlert (D16)
//
// Shared helper for the "Browser notifications" toggle. Single source
// of truth for the localStorage contract used by:
//   - src/components/PushHandler.tsx     (web-push path)
//   - src/pages/Settings.tsx            (toggle UI)
//   - src/pages/GuardDashboard.tsx      (local notification fallback)
//
// 3-state design (auto + manual override):
//   - 'auto' (default): follow isentialertLinked set by /api/setup/pair-claim
//                       '1' = iSentiAlert paired  -> web-push OFF
//                       '0' = no iSentiAlert      -> web-push ON
//   - 'on'   (manual): force on, ignore auto-detect
//   - 'off'  (manual): force off, ignore auto-detect
// =====================================================================

export const LS_OVERRIDE = 'browserNotificationsOverride';
export const LS_LINKED   = 'isentialertLinked';
export const LS_NOTIFIED_ALERT_IDS = 'lastNotifiedAlertIds';

export type EffectiveState = 'on' | 'off';

export interface NotificationState {
  override: 'on' | 'off' | null;
  linked:   '1' | '0' | null;
  effective: EffectiveState;
}

export function readBrowserNotificationsState(): NotificationState {
  const override = readOverride();
  const linked   = readLinked();
  const effective = resolveEffective(override, linked);
  return { override, linked, effective };
}

export function readOverride(): 'on' | 'off' | null {
  const v = localStorage.getItem(LS_OVERRIDE);
  if (v === 'on' || v === 'off') return v;
  return null;
}

export function readLinked(): '1' | '0' | null {
  const v = localStorage.getItem(LS_LINKED);
  if (v === '1' || v === '0') return v;
  return null;
}

export function resolveEffective(
  override: 'on' | 'off' | null,
  linked:   '1' | '0' | null,
): EffectiveState {
  if (override === 'on')  return 'on';
  if (override === 'off') return 'off';
  // Auto: linked === '1' means iSentiAlert is paired -> suppress web-push.
  return linked === '1' ? 'off' : 'on';
}

export function effectiveWebPushEnabled(): boolean {
  return resolveEffective(readOverride(), readLinked()) === 'on';
}

export function writeOverride(value: 'on' | 'off' | null): void {
  if (value === null) localStorage.removeItem(LS_OVERRIDE);
  else                localStorage.setItem(LS_OVERRIDE, value);
}

export function writeLinked(value: '1' | '0'): void {
  localStorage.setItem(LS_LINKED, value);
}

// Read the persisted "already notified" alert ID set, capped at 200
// to prevent localStorage bloat. Returns an empty set on first visit
// or parse error (caller handles the first-visit case).
export function readNotifiedAlertIds(): Set<string> {
  try {
    const stored = localStorage.getItem(LS_NOTIFIED_ALERT_IDS);
    if (!stored) return new Set();
    const arr = JSON.parse(stored);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x: unknown) => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

// Persist the updated set. Caps at 200 most recent IDs.
export function writeNotifiedAlertIds(ids: Iterable<string>): void {
  const arr = Array.from(ids);
  const capped = arr.length > 200 ? arr.slice(-200) : arr;
  try {
    localStorage.setItem(LS_NOTIFIED_ALERT_IDS, JSON.stringify(capped));
  } catch {
    // localStorage may be full or disabled (e.g., private mode) — best-effort
  }
}
